import { useAsyncCallback } from "../hooks/useAsyncCallback";
import { useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { createOrgSchema, type CreateOrgPayload } from "@io-spoke/contracts/orgs";
import { PageHeader } from "@components/layout/PageHeader";
import { PageShell } from "@components/layout/PageShell";
import { Button } from "@components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@components/ui/card";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import { toast } from "sonner";
import { createOrg } from "../lib/orgs";
import { useOrgContext } from "@components/layout/OrgContext";

type OrgSetupPageProps = {
  navigate: (route: string) => void;
};

export function OrgSetupPage({ navigate }: OrgSetupPageProps) {
  const { orgs, currentOrg, switchOrg, activateCreatedWorkspace, isLoading } = useOrgContext();
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    clearErrors,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<z.input<typeof createOrgSchema>, unknown, CreateOrgPayload>({
    resolver: zodResolver(createOrgSchema),
    mode: "onChange",
    defaultValues: { name: "" },
  });

  const activeOrgs = useMemo(
    () => orgs.filter((org) => org.membership.status === "active"),
    [orgs]
  );

  const {
    pending: createWorkspacePending,
    callback: createWorkspace,
  } = useAsyncCallback(async ({ name }: CreateOrgPayload) => {
    setSelectionError(null);
    try {
      const org = await createOrg({ name });
      activateCreatedWorkspace(org);
      toast.success(`${org.org.name} workspace created and activated.`);
      navigate("/settings/integrations");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to create workspace.";
      setError("root", { type: "server", message });
      toast.error(message);
    }
  });

  const {
    pending: handleSelectOrgPending,
    callback: handleSelectOrg,
  } = useAsyncCallback(async (orgId: string) => {
    setIsSelecting(true);
    setSelectionError(null);
    clearErrors("root");
    try {
      await switchOrg(orgId);
      toast.success("Workspace activated.");
      navigate("/dashboard");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to activate workspace.";
      setSelectionError(message);
      toast.error(message);
    } finally {
      setIsSelecting(false);
    }
  });

  const isSaving = isSubmitting || isSelecting;
  const pageError = selectionError ?? errors.root?.message;

  return (
    <PageShell maxWidth="lg">
      <PageHeader
        eyebrow="Workspace setup"
        title="Set up your workspace"
        description="Create a shared workspace for your customer evidence, obligations, and accountable actions."
      />

      <section className="mt-8 space-y-6">
        {pageError ? (
          <p id="workspace-error" className="text-sm text-destructive" role="alert">
            {pageError}
          </p>
        ) : null}

        {currentOrg ? (
          <Card className="border-border bg-card/60">
            <CardHeader>
              <CardTitle className="text-lg">Active workspace</CardTitle>
              <CardDescription>
                You already have an active workspace selected.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate("/dashboard")}>
                Go to dashboard
              </Button>
            </CardContent>
          </Card>
        ) : null}

        {activeOrgs.length > 0 && !currentOrg ? (
          <Card className="border-border bg-card/60">
            <CardHeader>
              <CardTitle className="text-lg">Choose a workspace</CardTitle>
              <CardDescription>
                Select one of your existing workspaces to continue.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {activeOrgs.map((org) => (
                <Button loading={handleSelectOrgPending} loadingLabel="Switching workspace…"
                  key={org.org.id}
                  variant="outline"
                  className="w-full justify-between"
                  onClick={() => handleSelectOrg(org.org.id)}
                  disabled={isSaving}
                >
                  {org.org.name}
                  <span className="text-xs text-muted-foreground">
                    {org.membership.role}
                  </span>
                </Button>
              ))}
            </CardContent>
          </Card>
        ) : null}

        <Card className="border-border bg-card/60">
          <CardHeader>
            <CardTitle className="text-lg">Create a workspace</CardTitle>
            <CardDescription>
              Use your company or team name. You can update it later.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleSubmit(createWorkspace)} noValidate>
              <div className="space-y-2">
                <Label htmlFor="org-name">Workspace name</Label>
                <Input
                  id="org-name"
                  {...register("name")}
                  placeholder="Acme"
                  disabled={isSaving || isLoading}
                  aria-describedby={errors.name ? "org-name-error" : undefined}
                  aria-invalid={Boolean(errors.name)}
                />
                {errors.name ? (
                  <p id="org-name-error" role="alert" className="text-sm text-destructive">
                    {errors.name.message}
                  </p>
                ) : null}
              </div>
              <Button loading={createWorkspacePending} loadingLabel="Creating workspace…" type="submit" disabled={isSaving || isLoading}>
                {isSubmitting ? "Creating workspace…" : "Create workspace"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </section>
    </PageShell>
  );
}
